/**
 * Provider de configuration de ressources multi-cloud
 * Permet de récupérer la configuration appropriée en fonction du type de ressource
 */

import {
  ResourceConfig as AzureResourceConfig,
  getResourceConfig as getAzureResourceConfig,
  getFieldSuggestions as getAzureFieldSuggestions
} from './azure/resourceConfig';

import {
  ResourceConfig as AwsResourceConfig,
  getResourceConfig as getAwsResourceConfig,
  getFieldSuggestions as getAwsFieldSuggestions
} from './aws/resourceConfig';

import {
  ResourceConfig as GcpResourceConfig,
  getResourceConfig as getGcpResourceConfig,
  getFieldSuggestions as getGcpFieldSuggestions
} from './gcp/resourceConfig';

import { getTerraformResourceName } from './resourceNameMapping';

// Type union pour les configurations
export type ResourceConfig = AzureResourceConfig | AwsResourceConfig | GcpResourceConfig;

// Déterminer le provider en fonction du préfixe du type de ressource
export const getProviderFromResourceType = (tfKey: string): 'azure' | 'aws' | 'gcp' | null => {
  if (tfKey.startsWith('azurerm_')) {
    return 'azure';
  } else if (tfKey.startsWith('aws_')) {
    return 'aws';
  } else if (tfKey.startsWith('google_')) {
    return 'gcp';
  }
  return null;
};

/**
 * Obtenir la configuration d'une ressource par son type Terraform
 * Détermine automatiquement le provider en fonction du préfixe
 */
export const getResourceConfig = (tfKey: string): ResourceConfig | null => {
  // Convertir le nom d'affichage en vrai nom Terraform
  const terraformName = getTerraformResourceName(tfKey);
  const provider = getProviderFromResourceType(terraformName);

  switch (provider) {
    case 'azure':
      return getAzureResourceConfig(terraformName);
    case 'aws':
      return getAwsResourceConfig(terraformName);
    case 'gcp':
      return getGcpResourceConfig(terraformName);
    default:
      return null;
  }
};

/**
 * Obtenir les suggestions pour un champ spécifique
 * Détermine automatiquement le provider en fonction du préfixe
 */
export const getFieldSuggestions = (tfKey: string, fieldKey: string): string[] => {
  // Convertir le nom d'affichage en vrai nom Terraform
  const terraformName = getTerraformResourceName(tfKey);
  const provider = getProviderFromResourceType(terraformName);

  switch (provider) {
    case 'azure':
      return getAzureFieldSuggestions(terraformName, fieldKey);
    case 'aws':
      return getAwsFieldSuggestions(terraformName, fieldKey);
    case 'gcp':
      return getGcpFieldSuggestions(terraformName, fieldKey);
    default:
      return [];
  }
};

/**
 * Obtenir toutes les configurations disponibles pour un provider
 */
export const getAllResourceConfigsForProvider = (provider: 'azure' | 'aws' | 'gcp'): Record<string, ResourceConfig> => {
  // Cette fonction pourrait être utile pour afficher toutes les ressources disponibles
  // Pour l'instant, on retourne un objet vide, mais on pourrait l'implémenter plus tard
  return {};
};
